import * as React from 'react'
import moment from 'moment'
import { useActivity  } from '@/modules/drivers/stores/activityStore'
import { useDate } from '@/modules/drivers/stores/dateStore'
import useViolationService from '../services/ViolationService'
import useDriverService from '../services/DriverService'
import { ViolationModel } from '@/app/models/violationModel'
import ViolationList from '../tabs/violations/components/ViolationList'
import ViolationSummary from '../tabs/violations/components/ViolationSummary'
import ViolationsActionBar from '../tabs/violations/components/ViolationsActionBar'
import ActivityChartWrapper from '../tabs/activities/components/charts/daily/ActivityChartWrapper'
import useActivityService from '../services/DriverActivityService'
import usePrintService from '../services/PrintService'
import { useTask } from '@/modules/shared/context/TaskContext'
import { TaskType } from '@/app/constants/taskType'
import { ReportType } from '@/app/constants/reportType'
import { toast } from '@/lib/toasts/api'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useViolationCalculationTask } from '@/modules/shared/hooks/useViolationCalculationTask'
import { addDays, format } from 'date-fns'
import { getViolationName } from '@/app/services/violation/violationNameResolver'
import { DatePickerRangeProps } from '@/modules/drivers/types'
import { SERVICE_DATE_FORMAT } from '@/app/constants/date'
import { generatePath } from 'react-router-dom'
import { driversRoutesMap } from '../routes'

const formatPln = (value: number): string => {
  const [intPart, decimalPart] = value.toFixed(2).split('.')
  return `${intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')},${decimalPart} PLN`
}

type ViolationFooterBarProps = {
  violations: ViolationModel[]
  selectedDate: DatePickerRangeProps
  daysWithContractInPeriod: number
}

// weight format: "driverWeight\companyWeight" e.g. "BPN\-", "-\BPN", "PN\PN", "-\-"
// CWPN/LNN use only the COMPANY weight (second segment after \)
const getCompanyWeight = (weight: string): string => {
  const parts = (weight || '').split('\\')
  const company = (parts[1] ?? '').trim()
  return company === '-' || company === '' ? '' : company
}

const ViolationFooterBar: React.FC<ViolationFooterBarProps> = ({
  violations,
  selectedDate,
  daysWithContractInPeriod,
}) => {
  const companyWeights = violations.map((v) => getCompanyWeight(v.weight))
  const count = (type: string) =>
    companyWeights.filter((w) => w === type).length

  const lnn = count('NN')
  const lbpn = count('BPN')
  const lpn = count('PN')

  const daysInPeriod = (() => {
    const { startDate, endDate } = selectedDate
    if (!startDate || !endDate) return 0
    const s = new Date(startDate.year, startDate.month - 1, startDate.day)
    const e = new Date(endDate.year, endDate.month - 1, endDate.day)
    return Math.round((e.getTime() - s.getTime()) / 86400000) + 1
  })()

  const cwpn =
    daysInPeriod > 0 && daysWithContractInPeriod > 0
      ? (lbpn + lpn / 3.0) / (daysWithContractInPeriod / daysInPeriod)
      : lbpn + lpn / 3.0

  const driverCharge = violations.reduce((s, v) => s + (v.driverCharge || 0), 0)
  const companyCharge = violations.reduce(
    (s, v) => s + (v.companyCharge || 0),
    0,
  )

  const fmt = (d: DatePickerRangeProps['startDate']) =>
    d
      ? `${String(d.day).padStart(2, '0')}.${String(d.month).padStart(2, '0')}.${d.year}`
      : '—'

  return (
    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-5 tw-border-t-2 tw-border-base-300 tw-bg-base-100 tw-px-4 tw-py-2 tw-text-[13px] tw-font-medium tw-text-[#343434]">
      <span>
        <span className="tw-font-normal tw-text-[#666]">CWPN: </span>
        <strong>{cwpn.toFixed(2).replace('.', ',')}</strong>
      </span>
      <span>
        <span className="tw-font-normal tw-text-[#666]">LNN: </span>
        <strong>{lnn}</strong>
      </span>
      <span className="tw-ml-auto tw-text-[#555]">
        {fmt(selectedDate?.startDate)} – {fmt(selectedDate?.endDate)}
      </span>
      <span>
        <span className="tw-font-normal tw-text-[#666]">Kara kierowcy: </span>
        <strong>{formatPln(driverCharge)}</strong>
      </span>
      <span>
        <span className="tw-font-normal tw-text-[#666]">Kara firmy: </span>
        <strong>{formatPln(companyCharge)}</strong>
      </span>
    </div>
  )
}

const getDaysWithContractInPeriod = (
  contracts: Array<{ startDate: string; endDate: string | null }>,
  periodStart: Date,
  periodEnd: Date,
): number => {
  const msPerDay = 86400000
  const totalDays =
    Math.round((periodEnd.getTime() - periodStart.getTime()) / msPerDay) + 1
  if (contracts.length === 0) return totalDays

  let count = 0
  for (let i = 0; i < totalDays; i++) {
    const day = new Date(
      periodStart.getFullYear(),
      periodStart.getMonth(),
      periodStart.getDate() + i,
    )
    const hasActive = contracts.some((c) => {
      const start = moment(c.startDate).startOf('day')
      const end = c.endDate ? moment(c.endDate).endOf('day') : null
      return (
        moment(day).isSameOrAfter(start) &&
        (end === null || moment(day).isSameOrBefore(end))
      )
    })
    if (hasActive) count++
  }
  return count
}

const ViolationsContent = ({ match, history, companyId, driverId }) => {
  const { setType } = useActivity()
  const { single, setSingle, formatApiDate } = useDate()
  const { getViolations, calculateViolations } = useViolationService()
  const { getDriverContracts } = useDriverService()
  const { getActivity } = useActivityService()
  const { getPdf } = usePrintService()
  const { addTask } = useTask()
  const { t } = useLocalize()
  const [violations, setViolations] = React.useState<ViolationModel[]>([])
  const [daysWithContractInPeriod, setDaysWithContractInPeriod] =
    React.useState<number>(0)
  const [confirmedViolations, setConfirmedViolations] = React.useState<
    number[]
  >([])
  const [selectedViolationDate, setSelectedViolationDate] = React.useState<
    string | null
  >(null)
  const [activities, setActivities] = React.useState([])
  const [search, setSearch] = React.useState('')

  // Get from/to from URL params
  const { params } = match
  const { from, to } = params

  // Convert string dates to DatePickerRangeProps format
  const convertToDatePickerProps = (
    dateString: string | undefined,
  ): DatePickerRangeProps['startDate'] | null => {
    if (!dateString || !dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return null
    }
    const parsed = moment(dateString, SERVICE_DATE_FORMAT, true)
    if (!parsed.isValid()) {
      return null
    }
    const date = parsed.toDate()
    if (isNaN(date.getTime())) {
      return null
    }
    return {
      fullDate: format(date, 'DD-MM-YY'),
      day: date.getDate(),
      month: date.getMonth() + 1,
      year: date.getFullYear(),
    }
  }

  const [selectedDate, setSelectedDate] = React.useState<DatePickerRangeProps>(
    () => {
      const start = convertToDatePickerProps(from)
      const end = convertToDatePickerProps(to)
      return {
        startDate: start,
        endDate: end,
      }
    },
  )

  // Update selected date when URL params change
  React.useEffect(() => {
    const start = convertToDatePickerProps(from)
    const end = convertToDatePickerProps(to)
    if (start && end) {
      setSelectedDate({
        startDate: start,
        endDate: end,
      })
    }
  }, [from, to])

  const handleDateRangeConfirm = ({
    startDate,
    endDate,
  }: DatePickerRangeProps) => {
    if (!startDate || !endDate) {
      return
    }

    // Validate that all required date fields are present
    if (
      startDate.year === null ||
      startDate.month === null ||
      startDate.day === null ||
      endDate.year === null ||
      endDate.month === null ||
      endDate.day === null
    ) {
      return
    }

    const newDateFrom = moment(
      `${startDate.year}-${String(startDate.month).padStart(2, '0')}-${String(startDate.day).padStart(2, '0')}`,
      'YYYY-MM-DD',
      true,
    )
    const newDateTo = moment(
      `${endDate.year}-${String(endDate.month).padStart(2, '0')}-${String(endDate.day).padStart(2, '0')}`,
      'YYYY-MM-DD',
      true,
    )

    // Validate that the parsed dates are valid before formatting
    if (!newDateFrom.isValid() || !newDateTo.isValid()) {
      return
    }

    // Update URL
    const newPath = generatePath(driversRoutesMap.driverViolations, {
      companyId,
      driverId,
      from: newDateFrom.format(SERVICE_DATE_FORMAT),
      to: newDateTo.format(SERVICE_DATE_FORMAT),
    })
    history.push(newPath)
  }

  // Helper function to format date for API calls (fixes date-fns format issue)
  const formatDateForApi = (date: Date) => {
    // Set time to 00:00:00
    const dateWithZeroTime = new Date(date)
    dateWithZeroTime.setHours(0, 0, 0, 0)
    // Use correct date-fns format tokens
    return encodeURIComponent(format(dateWithZeroTime, 'YYYY-MM-DD HH:mm'))
  }

  React.useEffect(() => {
    setType('daily')
  }, [])

  // Sync the "to" date to context so other views keep the user's current date
  // We use "to" (not "from") because "from" is "to - 1 month" and would shift the date backwards
  React.useEffect(() => {
    if (to && to.match(/^\d{4}-\d{2}-\d{2}$/)) {
      setSingle(to)
    }
  }, [to, setSingle])

  // Fetch violations when date changes
  React.useEffect(() => {
    const fetchViolations = async () => {
      if (!from || !to || !driverId) return

      try {
        // Validate date format - API expects YYYY-MM-DD (date only, no time)
        if (!from.match(/^\d{4}-\d{2}-\d{2}$/)) {
          console.error('Invalid from date format:', from)
          return
        }
        if (!to.match(/^\d{4}-\d{2}-\d{2}$/)) {
          console.error('Invalid to date format:', to)
          return
        }

        // Use from and to directly from URL - API expects YYYY-MM-DD format (no encoding needed)
        const startTime = from
        const endTime = to

        const result = await getViolations({
          companyId,
          driverId: parseInt(driverId),
          startTime,
          endTime,
        })
        // Map violations to resolve descriptions from type field
        const mappedViolations = (result || []).map((violation) => ({
          ...violation,
          description: getViolationName(violation.type),
        }))
        setViolations(mappedViolations)
      } catch (error) {
        console.error('Error fetching violations:', error)
        setViolations([])
      }
    }

    fetchViolations()
  }, [from, to, driverId, companyId])

  // Filter violations based on search
  const filteredViolations = React.useMemo(() => {
    if (!search || search.trim() === '') {
      return violations
    }
    const searchLower = search.toLowerCase().trim()
    return violations.filter((violation) =>
      violation.description?.toLowerCase().includes(searchLower),
    )
  }, [violations, search])

  const searchResults = filteredViolations.length

  // Fetch contracts and calculate days with active contract in period
  React.useEffect(() => {
    const fetchContracts = async () => {
      if (!from || !to || !driverId || !companyId) return
      try {
        const contracts = await getDriverContracts({ companyId, driverId })
        const [fy, fm, fd] = from.split('-').map(Number)
        const [ty, tm, td] = to.split('-').map(Number)
        const periodStart = new Date(fy, fm - 1, fd)
        const periodEnd = new Date(ty, tm - 1, td)
        setDaysWithContractInPeriod(
          getDaysWithContractInPeriod(contracts || [], periodStart, periodEnd),
        )
      } catch {
        setDaysWithContractInPeriod(0)
      }
    }
    fetchContracts()
  }, [from, to, driverId, companyId])

  // Fetch activities when violation is clicked
  const handleViolationClick = async (violation: ViolationModel) => {
    if (!violation.startTime) return

    try {
      const violationDate = moment(violation.startTime)
      const dateStr = format(violationDate.toDate(), 'YYYY-MM-DD')
      setSelectedViolationDate(dateStr)

      const dateObj = violationDate.toDate()
      const startTime = formatDateForApi(dateObj)
      const endTime = formatDateForApi(addDays(dateObj, 1))

      const result = await getActivity({
        companyId,
        driverId: parseInt(driverId),
        startTime,
        endTime,
        includeTrips: true,
      })
      setActivities(result?.activities ?? [])
    } catch (error) {
      console.error('Error fetching activities:', error)
    }
  }

  const handleCloseChart = () => {
    setSelectedViolationDate(null)
    setActivities([])
  }

  const startCalculation = React.useCallback(
    (requestId: string) => calculateViolations({ companyId, id: requestId }),
    [calculateViolations, companyId],
  )
  // Bez `scopeBusy`: ViolationsActionBar nie przyjmuje juz zadnych propsow, wiec
  // przycisku "Przelicz" na tym ekranie nie ma i nie ma czego blokowac. Handler
  // zostaje poprawny na wypadek, gdyby wrocil.
  const { calculate: handleCalculate } = useViolationCalculationTask({
    companyId,
    start: startCalculation,
  })

  const handlePrint = async () => {
    if (!single || !driverId) {
      toast.warning({
        text: t('driverModule.select-driver-and-date'),
      })
      return
    }

    try {
      // Parse manually by splitting the string (more reliable than new Date())
      const [year, month, day] = single.split('-').map(Number)
      const dateObj = new Date(year, month - 1, day)
      if (isNaN(dateObj.getTime())) {
        toast.error({
          text: t('common.invalid-date'),
        })
        return
      }
      const startTime = formatDateForApi(dateObj)
      const endTime = encodeURIComponent(`${format(dateObj, 'YYYY-MM-DD')} 23:59`)

      const taskId = await getPdf({
        companyId,
        reportType: ReportType.VIOLATIONS,
        drivers: [driverId],
        startTime,
        endTime,
        data: {
          violations:
            confirmedViolations.length > 0 ? confirmedViolations : undefined,
        },
      })
      addTask(taskId, TaskType.Printout, ReportType.VIOLATIONS)
    } catch (error) {
      console.error('Error printing violations:', error)
      toast.error({
        text: t('driverModule.report-generation-error'),
      })
    }
  }

  const handlePrintInstruction = async () => {
    if (!single || !driverId) {
      toast.warning({
        text: t('driverModule.select-driver-and-date'),
      })
      return
    }

    try {
      // Parse manually by splitting the string (more reliable than new Date())
      const [year, month, day] = single.split('-').map(Number)
      const dateObj = new Date(year, month - 1, day)
      if (isNaN(dateObj.getTime())) {
        toast.error({
          text: t('common.invalid-date'),
        })
        return
      }
      const startTime = formatDateForApi(dateObj)
      const endTime = encodeURIComponent(`${format(dateObj, 'YYYY-MM-DD')} 23:59`)

      const taskId = await getPdf({
        companyId,
        reportType: ReportType.DRIVER_INSTRUCTION,
        drivers: [driverId],
        startTime,
        endTime,
      })
      addTask(taskId, TaskType.Printout, ReportType.DRIVER_INSTRUCTION)
    } catch (error) {
      console.error('Error printing instruction:', error)
      toast.error({
        text: t('driverModule.instruction-generation-error'),
      })
    }
  }

  return (
    <div className="tw-flex tw-h-full tw-w-full tw-flex-col">
      <ViolationsActionBar
        onCalculate={handleCalculate}
        onPrint={handlePrint}
        onPrintInstruction={handlePrintInstruction}
        range={selectedDate}
        onDateRangeSubmit={handleDateRangeConfirm}
        search={search}
        setSearch={setSearch}
        searchResults={searchResults}
      />
      <div
        className={`tw-flex-1 tw-overflow-auto tw-bg-content-primary ${
          selectedViolationDate && activities.length > 0 ? 'tw-pb-[180px]' : ''
        }`}
      >
        {filteredViolations.length > 0 && (
          <>
            <ViolationSummary violations={filteredViolations} />
            <div className="tw-mt-2">
              <ViolationList
                violations={filteredViolations}
                onViolationClick={handleViolationClick}
                confirmedViolations={confirmedViolations}
                onConfirmedViolationsChange={setConfirmedViolations}
                companyId={companyId}
                driverId={driverId}
                history={history}
              />
            </div>
            <ViolationFooterBar
              violations={filteredViolations}
              selectedDate={selectedDate}
              daysWithContractInPeriod={daysWithContractInPeriod}
            />
          </>
        )}
        {filteredViolations.length === 0 && violations.length === 0 && (
          <div className="tw-flex tw-h-64 tw-items-center tw-justify-center">
            <p className="tw-text-content-base tw-opacity-70">
              {t('driverViolations.no-violations-for-day')}
            </p>
          </div>
        )}
        {filteredViolations.length === 0 && violations.length > 0 && (
          <div className="tw-flex tw-h-64 tw-items-center tw-justify-center">
            <p className="tw-text-content-base tw-opacity-70">
              {t('driverViolations.no-violations-matching-search')}
            </p>
          </div>
        )}
      </div>
      {selectedViolationDate && activities.length > 0 && (
        <div className="tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-z-50 tw-border-t tw-border-base-300 tw-bg-content-primary tw-shadow-lg">
          <div
            className="tw-p-2"
            style={{ maxHeight: '180px', overflow: 'hidden' }}
          >
            <div className="tw-mb-1 tw-flex tw-items-center tw-justify-between">
              <h3 className="tw-text-sm tw-font-medium">
                {t('driverViolations.activity-for-selected-violation')}
              </h3>
              <button
                onClick={handleCloseChart}
                className="tw-rounded tw-bg-primary tw-px-3 tw-py-1 tw-text-xs tw-text-white hover:tw-bg-primary/90"
              >
                Zamknij
              </button>
            </div>
            <div
              style={{
                height: '130px',
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              <style>{`
                .violation-activity-chart-wrapper > div {
                  min-height: 130px !important;
                  max-height: 130px !important;
                  overflow: hidden !important;
                }
              `}</style>
              <div
                className="violation-activity-chart-wrapper"
                style={{ height: '130px', overflow: 'hidden' }}
              >
                <ActivityChartWrapper
                  activities={activities}
                  currentDate={selectedViolationDate}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export const DriverViolationsPage = ({ match, history }) => {
  const { params } = match
  const { driverId, companyId, from, to } = params
  const { getDateRangeForView } = useDate()

  // Redirect to route with date parameters if they're missing
  React.useEffect(() => {
    if (driverId && companyId && (!from || !to)) {
      const { from: defaultFrom, to: defaultTo } = getDateRangeForView()
      const newPath = `/et/company/${companyId}/driverModule/driver/${driverId}/violation/from/${defaultFrom}/to/${defaultTo}`
      history.replace(newPath)
    }
  }, [driverId, companyId, from, to, history])

  return (
      <ViolationsContent
        match={match}
        history={history}
        companyId={companyId}
        driverId={driverId}
      />
  )
}
